import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Award, Wallet as WalletIcon, ShieldAlert, Phone, KeyRound } from "lucide-react";
import { toast } from "sonner";

import { get } from "@/lib/api-client";
import { AdminAPI } from "@/lib/admin-api";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Skeleton } from "@/components/ui/Skeleton";
import { StatusPill } from "@/components/ui/StatusPill";
import { Badge } from "@/components/ui/Badge";
import { Tabs } from "@/components/ui/Tabs";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { formatCurrency, formatDateTime, formatNumber } from "@/lib/format";
import { AdjustBalanceModal } from "./components/AdjustBalanceModal";
import type { User } from "@/types/auth";
import type { ClubEntry, QualificationSummary } from "@/types/club";
import type { Wallet, LedgerEntry } from "@/types/wallet";

export function UserDetailPage() {
  const { userId } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState("entries");
  const [adjustModal, setAdjustModal] = useState<"qualification" | "wallet" | null>(null);
  
  // 🛠️ NEW: Modals State
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const user = useQuery({
    queryKey: ["admin-user", userId],
    queryFn: () => get<User>(`/admin/users/${userId}`),
    enabled: !!userId,
  });
  const entries = useQuery({
    queryKey: ["admin-user-entries", userId],
    queryFn: () => get<ClubEntry[]>(`/admin/users/${userId}/entries`),
    enabled: !!userId,
  });
  const wallet = useQuery({
    queryKey: ["admin-user-wallet", userId],
    queryFn: async () => {
      try { return await get<Wallet>(`/admin/users/${userId}/wallet`); }
      catch (err: any) { if (err?.response?.status === 404) return null; throw err; }
    },
    enabled: !!userId,
  });
  const qual = useQuery({
    queryKey: ["admin-user-qual", userId],
    queryFn: () => get<QualificationSummary>(`/admin/users/${userId}/qualification`),
    enabled: !!userId,
  });
  const ledger = useQuery({
    queryKey: ["admin-user-ledger", userId],
    queryFn: () => get<LedgerEntry[]>(`/admin/users/${userId}/ledger`),
    enabled: !!userId && tab === "ledger",
  });

  // 🛠️ Mutations
  const updatePhone = useMutation({
    mutationFn: () => AdminAPI.updateUserPhone(userId!, newPhone),
    onSuccess: () => {
      toast.success("Phone number updated successfully.");
      qc.invalidateQueries({ queryKey: ["admin-user", userId] });
      setPhoneModalOpen(false);
      setNewPhone("");
    }
  });

  const resetPassword = useMutation({
    mutationFn: () => AdminAPI.resetUserPassword(userId!, newPassword),
    onSuccess: () => {
      toast.success("Password reset successfully. User has been logged out.");
      setPasswordModalOpen(false);
      setNewPassword("");
    }
  });

  if (user.isLoading || !user.data) return <Skeleton className="h-96 rounded-xl" />;
  const u = user.data;

  return (
    <div className="space-y-6 animate-fade-in">
      <button onClick={() => nav(-1)}
              className="text-sm text-surface-500 hover:text-surface-800 inline-flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Back to Users
      </button>

      <Card>
        <CardBody className="flex flex-wrap items-center gap-5">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 grid place-items-center text-white text-2xl font-bold shadow-md">
            {u.full_name.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xl font-bold text-surface-900">{u.full_name}</div>
            <div className="text-sm text-surface-500 font-mono mt-1">
              <span className="font-bold text-brand-600">{u.user_id}</span> • {u.mobile}
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <Badge tone="info" className="capitalize">{u.role.replace(/_/g, " ")}</Badge>
              <StatusPill status={u.status} />
              <span className="text-xs text-surface-500">Joined {formatDateTime(u.created_at)}</span>
            </div>
          </div>
          
          {/* 🛠️ Action Buttons Group */}
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <Button size="sm" variant="outline" leftIcon={<Award className="h-4 w-4" />}
                      onClick={() => setAdjustModal("qualification")}>
                Adjust Points
              </Button>
              <Button size="sm" variant="outline" leftIcon={<WalletIcon className="h-4 w-4" />}
                      onClick={() => setAdjustModal("wallet")}>
                Adjust Wallet
              </Button>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" leftIcon={<Phone className="h-4 w-4 text-surface-500" />}
                      onClick={() => { setNewPhone(u.mobile); setPhoneModalOpen(true); }}>
                Change Phone
              </Button>
              <Button size="sm" variant="danger" leftIcon={<KeyRound className="h-4 w-4" />}
                      onClick={() => { setNewPassword(""); setPasswordModalOpen(true); }}>
                Reset Password
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      <div className="grid gap-4 md:grid-cols-4">
        <StatBox label="Available Points" value={qual.data ? formatNumber(qual.data.available) : "—"} tone="success" />
        <StatBox label="Locked Points"    value={qual.data ? formatNumber(qual.data.locked) : "—"}    tone="info" />
        <StatBox label="Withdrawable"     value={wallet.data ? formatCurrency(wallet.data.withdrawable) : "$0.00"} tone="success" />
        <StatBox label="Total Earned"     value={wallet.data ? formatCurrency(wallet.data.total_earned) : "$0.00"} tone="neutral" />
      </div>

      <Card>
        <div className="px-5 pt-3">
          <Tabs value={tab} onChange={setTab}
                tabs={[
                  { value: "entries", label: "Club Entries", count: entries.data?.length },
                  { value: "ledger",  label: "Ledger" },
                ]} />
        </div>
        <CardBody className="p-0">
          {/* ... Rest of the table code remains unchanged ... */}
          {tab === "entries" && (
            entries.isLoading ? (
              <div className="p-5 space-y-3">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
              </div>
            ) : entries.data && entries.data.length ? (
              <Table>
                <THead>
                  <TR>
                    <TH>Entry ID</TH><TH>Method</TH><TH>Status</TH>
                    <TH>Placement</TH><TH>Created</TH>
                  </TR>
                </THead>
                <tbody>
                  {entries.data.map((e) => (
                    <TR key={e.entry_id}>
                      <TD className="font-mono text-xs font-bold text-brand-700">{e.entry_id}</TD>
                      <TD className="capitalize">{e.creation_method}</TD>
                      <TD><StatusPill status={e.status} /></TD>
                      <TD><StatusPill status={e.placement_status} /></TD>
                      <TD className="text-xs">{formatDateTime(e.created_at)}</TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            ) : <div className="p-8 text-center text-sm text-surface-500">No entries yet.</div>
          )}
          
          {tab === "ledger" && (
            ledger.isLoading ? (
              <div className="p-5 space-y-3">
                {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
              </div>
            ) : ledger.data && ledger.data.length ? (
              <Table>
                <THead>
                  <TR>
                    <TH>Ledger ID</TH><TH>Type</TH><TH>Description</TH>
                    <TH>Debit</TH><TH>Credit</TH>
                    <TH className="text-right">Balance</TH><TH>When</TH>
                  </TR>
                </THead>
                <tbody>
                  {ledger.data.map((l) => (
                    <TR key={l.ledger_id}>
                      <TD className="font-mono text-2xs">{l.ledger_id}</TD>
                      <TD><StatusPill status={l.entry_type} /></TD>
                      <TD className="text-xs max-w-[200px] truncate">{l.description}</TD>
                      <TD className="text-danger-600 text-xs font-bold">
                        {Number(l.debit) > 0 ? formatCurrency(l.debit) : "—"}
                      </TD>
                      <TD className="text-success-700 text-xs font-bold">
                        {Number(l.credit) > 0 ? formatCurrency(l.credit) : "—"}
                      </TD>
                      <TD className="text-right font-bold text-surface-900">{formatCurrency(l.balance_after)}</TD>
                      <TD className="text-2xs text-surface-500 whitespace-nowrap">{formatDateTime(l.created_at)}</TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            ) : <div className="p-8 text-center text-sm text-surface-500">No ledger entries.</div>
          )}
        </CardBody>
      </Card>

      {/* MODALS */}
      {adjustModal && (
        <AdjustBalanceModal
          open={!!adjustModal}
          onClose={() => setAdjustModal(null)}
          userId={u.user_id}
          kind={adjustModal}
        />
      )}

      {/* 🛠️ NEW: Change Phone Modal */}
      <Modal open={phoneModalOpen} onClose={() => setPhoneModalOpen(false)} title={`Change Phone Number`}>
        <div className="space-y-4">
          <Input 
            label="New Mobile Number" 
            placeholder="e.g. +8801700000000" 
            value={newPhone} 
            onChange={(e) => setNewPhone(e.target.value)} 
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setPhoneModalOpen(false)}>Cancel</Button>
            <Button loading={updatePhone.isPending} disabled={!newPhone.trim()} onClick={() => updatePhone.mutate()}>Save Phone</Button>
          </div>
        </div>
      </Modal>

      {/* 🛠️ NEW: Reset Password Modal */}
      <Modal open={passwordModalOpen} onClose={() => setPasswordModalOpen(false)} title={`Reset Password for ${u.user_id}`}>
        <div className="space-y-4">
          <div className="bg-danger-50 border border-danger-200 text-danger-800 p-3 rounded-lg text-sm">
            Warning: Changing the password will force the user to log out from all devices immediately.
          </div>
          <Input 
            label="New Password" 
            type="text" 
            placeholder="Enter minimum 6 characters" 
            value={newPassword} 
            onChange={(e) => setNewPassword(e.target.value)} 
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setPasswordModalOpen(false)}>Cancel</Button>
            <Button variant="danger" loading={resetPassword.isPending} disabled={newPassword.length < 6} onClick={() => resetPassword.mutate()}>Force Reset</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function StatBox({ label, value, tone }: { label: string; value: string; tone: "info" | "success" | "neutral" }) {
  const TONE = {
    info:    "bg-brand-50 border-brand-200 text-brand-700",
    success: "bg-success-50 border-success-200 text-success-700",
    neutral: "bg-surface-50 border-surface-200 text-surface-700",
  };
  return (
    <div className={`rounded-2xl border p-5 shadow-sm ${TONE[tone]}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-80 mb-1">{label}</div>
      <div className="text-2xl font-black">{value}</div>
    </div>
  );
}